import {
  buildServiceTree,
  indexIncluded,
  mapPrice,
  resolveRef,
  ServiceTreeBuilders
} from 'Douleutaras/functional-components/ServicePrices/servicePriceTree';
import { ServicePriceIncludedResource } from 'Douleutaras/functional-components/ServicePrices/types';

import {
  SettingsService,
  SettingsServicePriceResource,
  SettingsServicePricesDocument,
  SettingsServicesCategory
} from './types';

const SERVICE_PRICE_TYPE = 'seller-service-price';

const isPriceResource = (
  resource: SettingsServicePriceResource | ServicePriceIncludedResource
): resource is SettingsServicePriceResource => resource.type === SERVICE_PRICE_TYPE;

const isLabelResource = (
  resource: SettingsServicePriceResource | ServicePriceIncludedResource
): resource is ServicePriceIncludedResource => resource.type !== SERVICE_PRICE_TYPE;

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const settingsBuilders: ServiceTreeBuilders<SettingsServicePriceResource, SettingsService, SettingsServicesCategory> = {
  createNode: ({ serviceId, level, label }) => ({ serviceId, level, label, isHidden: false }),
  applyRow: (node, { id, attributes }) => ({
    ...node,
    priceId: id,
    isHidden: attributes.hidden,
    price: attributes.status === 'priced' ? mapPrice(attributes) : undefined,
    marketRange: attributes.marketRange ?? undefined,
    marketRangeLabel: attributes.marketRangeLabel ?? undefined
  }),
  // Raw id, so the accordions keep their keys when the loaded list replaces the placeholders
  createCategory: (category, _categoryId, services) => ({ categoryId: category.id, title: category.title, services })
};

/**
 * Maps the pro's `user/settings/service-prices/` document into the settings accordions: every
 * vetted category in API order (empty ones included), its prices oldest first, details nested
 * under their service.
 */
export const mapServicePriceSettings = (doc: SettingsServicePricesDocument): Array<SettingsServicesCategory> => {
  const included = doc.included ?? [];
  const priceById = new Map(included
    .filter(isPriceResource)
    .map(price => [price.id, price]));
  // Categories come in `data`, so they are indexed alongside the sideloaded labels
  const resourceByRef = indexIncluded([...doc.data, ...included.filter(isLabelResource)]);

  const seedCategories = doc.data
    .map(category => resolveRef(resourceByRef, { data: { type: category.type, id: category.id } }))
    .filter(isDefined);

  const rows = doc.data
    .flatMap(category => category.relationships?.prices?.data ?? [])
    .map(link => priceById.get(link.id))
    .filter(isDefined);

  return buildServiceTree(rows, resourceByRef, settingsBuilders, seedCategories);
};

const isUnpriced = (service: SettingsService): boolean => service.priceId !== undefined && service.price === undefined;

const withoutUnpricedService = (service: SettingsService): SettingsService | undefined => {
  const children = (service.children ?? []).filter(child => !isUnpriced(child));
  const { children: _children, ...node } = service;

  if (isUnpriced(service)) {
    // Kept only as the parent of its priced details
    return children.length > 0
      ? { serviceId: node.serviceId, level: node.level, label: node.label, isHidden: false, children }
      : undefined;
  }

  if (node.priceId === undefined && children.length === 0) {
    return undefined;
  }

  return children.length > 0 ? { ...node, children } : node;
};

/** Drops services listed without a price, for when `unpricedServicesEnabled` is off. */
export const withoutUnpricedServices = (categories: Array<SettingsServicesCategory>): Array<SettingsServicesCategory> =>
  categories.map(category => ({
    ...category,
    services: category.services
      .map(withoutUnpricedService)
      .filter(isDefined)
  }));
